import { store } from '../redux/store';
import ApiHelper from './apiHelper';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

const MFA_STATUS = {
  ENABLE: 'enable',
  DISABLE: 'disable',
};

const AuthHelper = {
  /**
   * @typedef {'default' | 'lead' | 'demo'} Environment
   * @typedef {'super_admin' | 'admin' | 'ae' | 'user' | null} Access
   *
   * @param {Access | Access[] | null} access
   * @param {object} user
   * @param {Environment | Environment[] | null} environments
   * @returns {boolean} indicating if the user should have access
   */
  hasAccess: (
    access,
    user = store.getState().auth.user,
    { environments = null } = {},
  ) => {
    // If no access is defined, everyone can access it
    if (!access) {
      return true;
    }

    if (!user) {
      return false;
    }

    // Check environment access
    if (environments) {
      const currentEnvironment = process.env.REACT_APP_ENVIRONMENT;
      if (Array.isArray(environments)) {
        if (!environments.includes(currentEnvironment)) {
          return false;
        }
      } else if (environments !== currentEnvironment) {
        return false;
      }
    }

    // Remove super admin only pages if the user is not super admin
    if (
      access === 'super_admin' ||
      (Array.isArray(access) && access.includes('super_admin'))
    ) {
      if (user.is_superuser) {
        return true;
      }
    }

    // AE is a special kind of user
    const userRole =
      user.profile.user_type === 0
        ? 'admin'
        : user.profile.is_amz_ae
          ? 'ae'
          : 'user';

    if (Array.isArray(access)) {
      return access.includes(userRole);
    }

    // AE can access everything a user can
    if (access === 'user' && userRole === 'ae') {
      return true;
    }

    return access === userRole;
  },

  useHasAccess: (access, { environments = null } = {}) => {
    const user = useSelector((state) => state.auth.user);
    return useMemo(
      () =>
        AuthHelper.hasAccess(access, user, {
          environments,
        }),
      [access, environments, user],
    );
  },

  login: (username, password) => {
    return ApiHelper.post(
      '/api/auth/login/',
      { username, password },
      {},
      false,
    );
  },

  logout: () => {
    return ApiHelper.post('/api/auth/logout/');
  },

  signup: (email, username, password) => {
    return ApiHelper.post(
      '/api/auth/signup/',
      { email, username, password },
      {},
      false,
    );
  },

  getUserInfo: () => {
    return ApiHelper.get('/api/auth/user/');
  },

  getAllUsers: () => {
    return ApiHelper.get('/api/auth/user_list/');
  },

  accessToken: () => {
    let state = store.getState();
    if (state.auth) {
      return state.auth.token;
    }
    return null;
  },

  isAuthenticated: (state) => {
    return !!state.token;
  },

  changePassword: (currentPassword, newPassword) => {
    return ApiHelper.post('/api/auth/password-change/', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  updateProfile: (profile) => {
    return ApiHelper.patch('/api/auth/user/', profile);
  },

  getMFASetup: () => {
    return ApiHelper.post('/api/auth/mfa/request/');
  },

  verifyMFA: (token, status = MFA_STATUS.ENABLE) => {
    return ApiHelper.post('/api/auth/mfa/verify/', { token, status });
  },

  loginVerifyMFA: (token) => {
    return ApiHelper.post(
      '/api/auth/mfa/verify/public/',
      { token },
      {},
      false,
    );
  },
};

export default AuthHelper;
export { MFA_STATUS };
